// Schema.org (JSON-LD) builders.
//
// Everything is wired together with @id references so search engines and AI
// answer engines resolve one clinic entity across every page instead of
// treating each page as an unrelated business.

import {
  SITE_URL,
  BUSINESS,
  DENTIST,
  DEFAULT_OG_IMAGE,
  absoluteUrl,
} from "./siteConfig";

export const ORG_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const DENTIST_ID = `${SITE_URL}/#dr-aishwarya-sarda`;

function openingHoursSpecification() {
  return BUSINESS.openingHours.map((slot) => ({
    "@type": "OpeningHoursSpecification",
    dayOfWeek: slot.days,
    opens: slot.opens,
    closes: slot.closes,
  }));
}

/**
 * The clinic itself. `Dentist` is a subtype of both LocalBusiness and
 * MedicalBusiness, so this single node covers local-pack eligibility and
 * medical-entity understanding.
 */
export function localBusinessSchema() {
  const hours = openingHoursSpecification();

  return {
    "@type": ["Dentist", "MedicalClinic"],
    "@id": ORG_ID,
    name: BUSINESS.name,
    legalName: BUSINESS.legalName,
    description: BUSINESS.description,
    url: SITE_URL,
    telephone: BUSINESS.telephone,
    priceRange: BUSINESS.priceRange,
    image: DEFAULT_OG_IMAGE,
    logo: {
      "@type": "ImageObject",
      "@id": `${SITE_URL}/#logo`,
      url: `${SITE_URL}/logo.png`,
      caption: BUSINESS.name,
    },
    hasMap: BUSINESS.mapUrl,
    address: {
      "@type": "PostalAddress",
      streetAddress: BUSINESS.address.street,
      addressLocality: `${BUSINESS.address.locality}, ${BUSINESS.city}`,
      addressRegion: BUSINESS.address.region,
      postalCode: BUSINESS.address.postalCode,
      addressCountry: BUSINESS.address.country,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: BUSINESS.geo.latitude,
      longitude: BUSINESS.geo.longitude,
    },
    areaServed: BUSINESS.areaServed.map((name) => ({
      "@type": "City",
      name,
    })),
    ...(hours.length ? { openingHoursSpecification: hours } : {}),
    sameAs: BUSINESS.sameAs,
    founder: { "@id": DENTIST_ID },
    employee: [{ "@id": DENTIST_ID }],
    medicalSpecialty: "Dentistry",
    availableService: SERVICES.map((s) => ({
      "@type": "MedicalProcedure",
      name: s.name,
      url: absoluteUrl(s.path),
    })),
    potentialAction: {
      "@type": "ReserveAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/#book`,
        inLanguage: "en-IN",
        actionPlatform: [
          "http://schema.org/DesktopWebPlatform",
          "http://schema.org/MobileWebPlatform",
        ],
      },
      result: { "@type": "Reservation", name: "Dental appointment" },
    },
  };
}

/**
 * The treating dentist — the E-E-A-T signal that AI engines cite by name.
 *
 * Dual-typed on purpose: `Person` is what Google expects for content authors
 * and `reviewedBy`, while `Physician` carries the clinical properties
 * (medicalSpecialty, address) that medical-entity parsers look for.
 */
export function dentistSchema() {
  return {
    "@type": ["Person", "Physician"],
    "@id": DENTIST_ID,
    name: DENTIST.name,
    jobTitle: DENTIST.jobTitle,
    description: DENTIST.description,
    medicalSpecialty: "Dentistry",
    knowsAbout: [
      "Endodontics",
      "Root canal treatment",
      "Conservative dentistry",
      "Restorative dentistry",
      "Dental implants",
      "Cosmetic dentistry",
    ],
    hasCredential: {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "degree",
      name: DENTIST.qualification,
    },
    telephone: BUSINESS.telephone,
    address: {
      "@type": "PostalAddress",
      streetAddress: BUSINESS.address.street,
      addressLocality: `${BUSINESS.address.locality}, ${BUSINESS.city}`,
      addressRegion: BUSINESS.address.region,
      postalCode: BUSINESS.address.postalCode,
      addressCountry: BUSINESS.address.country,
    },
    worksFor: { "@id": ORG_ID },
    url: SITE_URL,
  };
}

export function webSiteSchema() {
  return {
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    url: SITE_URL,
    name: BUSINESS.name,
    description: BUSINESS.description,
    inLanguage: "en-IN",
    publisher: { "@id": ORG_ID },
  };
}

export function webPageSchema({ url, title, description, image, datePublished, dateModified }) {
  return {
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: title,
    description,
    isPartOf: { "@id": WEBSITE_ID },
    about: { "@id": ORG_ID },
    inLanguage: "en-IN",
    ...(image ? { primaryImageOfPage: { "@type": "ImageObject", url: image } } : {}),
    ...(datePublished ? { datePublished } : {}),
    ...(dateModified ? { dateModified } : {}),
  };
}

/**
 * Medical/procedure pages get MedicalWebPage rather than plain WebPage — it
 * tells engines the content is clinical and names a reviewing clinician.
 */
export function medicalWebPageSchema({ url, title, description, procedureName }) {
  return {
    "@type": "MedicalWebPage",
    "@id": `${url}#webpage`,
    url,
    name: title,
    description,
    isPartOf: { "@id": WEBSITE_ID },
    inLanguage: "en-IN",
    about: {
      "@type": "MedicalProcedure",
      name: procedureName,
      procedureType: "http://schema.org/TherapeuticProcedure",
      howPerformed: description,
      provider: { "@id": ORG_ID },
    },
    author: { "@id": DENTIST_ID },
    reviewedBy: { "@id": DENTIST_ID },
    specialty: "Dentistry",
    ...(LAST_REVIEWED ? { lastReviewed: LAST_REVIEWED } : {}),
  };
}

/** Offered treatment, linked back to the clinic as provider. */
export function serviceSchema({ url, name, description }) {
  return {
    "@type": "MedicalTherapy",
    "@id": `${url}#service`,
    name,
    description,
    provider: { "@id": ORG_ID },
    relevantSpecialty: "Dentistry",
    availableAtOrFrom: { "@id": ORG_ID },
  };
}

/**
 * FAQ markup. Only ever call this with Q&A that is also visible on the page —
 * Google treats hidden-only FAQ markup as a structured-data violation.
 */
export function faqSchema(items, url) {
  if (!items || !items.length) return null;
  return {
    "@type": "FAQPage",
    "@id": `${url}#faq`,
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
}

export function breadcrumbSchema(crumbs, url) {
  if (!crumbs || crumbs.length < 2) return null;
  return {
    "@type": "BreadcrumbList",
    "@id": `${url}#breadcrumb`,
    itemListElement: crumbs.map((crumb, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: crumb.name,
      item: absoluteUrl(crumb.path),
    })),
  };
}

export function articleSchema({ url, title, description, image, datePublished, dateModified, author }) {
  return {
    "@type": "BlogPosting",
    "@id": `${url}#article`,
    headline: title,
    description,
    ...(image ? { image } : {}),
    ...(datePublished ? { datePublished } : {}),
    dateModified: dateModified || datePublished,
    author: author === DENTIST.name || !author
      ? { "@id": DENTIST_ID }
      : { "@type": "Person", name: author },
    publisher: { "@id": ORG_ID },
    mainEntityOfPage: { "@id": `${url}#webpage` },
    inLanguage: "en-IN",
  };
}

/** Wrap nodes into a single @graph document — one script tag per page. */
export function graph(nodes) {
  return {
    "@context": "https://schema.org",
    "@graph": nodes.filter(Boolean),
  };
}

// Date the treatment pages were last clinically reviewed by Dr. Sarda, as
// "YYYY-MM-DD". Emitted as schema.org `lastReviewed`, a strong E-E-A-T signal
// for medical content — but only set it once a review has actually happened,
// and re-set it each time the content is reviewed again.
export const LAST_REVIEWED = "";

// Canonical list of treatments — drives navigation schema, the clinic's
// availableService list, and the sitemap.
export const SERVICES = [
  { name: "Dental Fillings", path: "/dental-filling" },
  { name: "Wisdom Teeth Removal", path: "/wisdom-teeth-removal" },
  { name: "Root Canal Treatment", path: "/root-canal-treatment" },
  { name: "Braces and Aligners", path: "/braces-and-aligners" },
  { name: "Dental Implants", path: "/dental-implants" },
  { name: "Dentures", path: "/dentures" },
  { name: "Dental Bridges and Crowns", path: "/bridges-and-crowns" },
  { name: "Kids Dentistry", path: "/kids-dentistry" },
  { name: "Mouth Ulcer Treatment", path: "/mouth-ulcers" },
];
